import React, { forwardRef, Fragment, ReactNode } from 'react';
import { Typography } from '@mui/material';
import { Collapse } from 'src/components/Collapse';
import {
  Container,
  InnerContainer,
  MetricName,
  SubGroupName,
} from 'src/components/Forms/MetricsDropdown/styled';
import { FormattedCustomFormulaItem } from 'src/utils';
import {
  ConnectorType,
  Metric,
  MetricsByConnectorType,
} from 'src/utils/gql/types';

interface MetricsDropdownProps {
  type: ConnectorType | MetricsByConnectorType;
  label: string;
  icon: ReactNode;
  expanded: boolean;
  metricsByCategory: Record<string, Metric[]>;
  chosenMetrics?: FormattedCustomFormulaItem[];
  loading?: boolean;
  onClickMetric?: (metric: Metric) => void;
  onToggle: (type: ConnectorType | MetricsByConnectorType) => void;
  totalMetricsCount?: number;
  searchQuery?: string;
}

export const MetricsDropdown = forwardRef<HTMLDivElement, MetricsDropdownProps>(
  (
    {
      type,
      label,
      icon,
      expanded,
      metricsByCategory,
      chosenMetrics = [],
      loading,
      onToggle,
      onClickMetric,
      totalMetricsCount = 0,
      searchQuery = '',
    },
    ref,
  ) => {
    const toggleCollapse = () => onToggle(type);

    return (
      <Collapse
        title={label}
        icon={icon}
        expanded={expanded}
        onToggle={toggleCollapse}
        loading={loading}
        ref={ref}
      >
        <Container>
          {!Object.entries(metricsByCategory).length && !loading && (
            <Typography m="auto">
              {totalMetricsCount > 0 && searchQuery.trim()
                ? `No metrics match "${searchQuery.trim()}"`
                : 'No metrics'}
            </Typography>
          )}
          {Object.entries(metricsByCategory).map(
            ([category, metrics], index) => (
              <Fragment key={index}>
                <SubGroupName variant="body2">{category}</SubGroupName>
                <InnerContainer>
                  {metrics.map((metric, index) => (
                    <MetricName
                      key={index}
                      onClick={() => onClickMetric?.(metric)}
                      active={
                        +chosenMetrics?.some(
                          (chosenMetric) => chosenMetric.value === metric.key,
                        )
                      }
                    >
                      {metric.label}
                    </MetricName>
                  ))}
                </InnerContainer>
              </Fragment>
            ),
          )}
        </Container>
      </Collapse>
    );
  },
);
